import getCurrentLanguageShort from './getLanguage';
import { isPageTypePatient, isPageTypeHcp } from './getTypeOfPage';
import {
  getLinkWithParametersAllProps,
  getLinkWithParametersProps,
} from '../interfaces/interfaces';

interface linkMultilingualDataType {
  linkWithoutLanguage: string;
  currentLanguageSubDomain: string;
  currentLanguageSubDomainWithSlash: string;
  isMultilingual: boolean;
  patientProposalCurrentPageLink: string;
  patientProposalCurrentPageLinkWithoutLang: string;
  patientMainPageLink: string;
  hcpProposalCurrentPageLink: string;
  hcpProposalCurrentPageLinkWithoutLang: string;
  patientMainPageLinkNormalized: string;
  patientMainPageLinkNormalizedWithExternal: string;
  linkWithoutLanguageAndRole: string;
}

interface addExternalLinkAttributeProps {
  el: HTMLElement;
  selector: string;
}

export interface LinkPathnameLanguageDataType {
  isMultilingual: boolean;
  currentLanguageSubDomain: string;
  currentLanguageSubDomainWithSlash: string;
}

interface LinkPathnameDataWithoutLangType {
  isMultilingual: boolean;
  arrayLinkWithoutLanguage: string[];
  linkWithoutLanguage: string;
}

// add leading slash if link is not external and not starts from slash.
// Not include file links for example file.pdf
export const leadingSlashNormalize = (link: string): string => {
  if (!link) {
    return '';
  }

  if (link.includes('.')) {
    return link;
  }

  return link.startsWith('/') ? link : `/${link}`;
};

// add leading slash if link is not external and not starts from slash.
export const leadingSlashRegexNormalize = (link: string): string => {
  if (!link) {
    return '';
  }

  return link.replace(/^\/?(?!www\.)([^\/]+(?:\/[^\/]+)*)\/?$/, '/$1');
};

export const slashTrim = (item: string): string =>
  item
    ?.trim()
    .split('/')
    .filter(x => x)
    .join('/');

export const getLinkPathnameLanguageData = (
  pathnameCustom?: string,
): LinkPathnameLanguageDataType => {
  const pathName =
    (pathnameCustom && typeof pathnameCustom === 'string' && decodeURI(pathnameCustom)) ||
    decodeURI(window.location.pathname);
  const linkNormalized = slashTrim(pathName);
  const subDomainsArray = linkNormalized?.split('/');
  const isMultilingual =
    !!window.multilingualData?.forceMultilingual ||
    subDomainsArray[0] === getCurrentLanguageShort();

  const currentLanguageSubDomainSource = !!window.multilingualData
    ?.getCurrentLanguageSubDomainFromPageCode
    ? getCurrentLanguageShort()
    : subDomainsArray[0];

  const currentLanguageSubDomain = isMultilingual ? currentLanguageSubDomainSource : '';
  const currentLanguageSubDomainWithSlash = currentLanguageSubDomain.length
    ? `${currentLanguageSubDomain}/`
    : '';

  return {
    isMultilingual,
    currentLanguageSubDomain,
    currentLanguageSubDomainWithSlash,
  };
};

export const getLinkPathnameDataWithoutLang = (
  pathnameCustom?: string,
): LinkPathnameDataWithoutLangType => {
  const pathName =
    (pathnameCustom && typeof pathnameCustom === 'string' && decodeURI(pathnameCustom)) ||
    decodeURI(window.location.pathname);
  const { isMultilingual } = getLinkPathnameLanguageData(pathName);
  const linkNormalized = slashTrim(pathName);
  const subDomainsArray = linkNormalized?.split('/');
  const arrayLinkWithoutLanguage = isMultilingual ? subDomainsArray.slice(1) : subDomainsArray;
  const linkWithoutLanguage = arrayLinkWithoutLanguage.join('/');

  return {
    isMultilingual,
    arrayLinkWithoutLanguage,
    linkWithoutLanguage,
  };
};

export const isLinkExternal = (link: string): boolean => {
  if (link?.length) {
    return link.includes('.') && !link.includes(decodeURI(window.location.hostname));
  }
  return false;
};

export const isLinkNotInWhiteList = (link: string, whitelist: object): boolean => {
  if (link?.length) {
    const urlObject = new URL(link);
    const url = urlObject.hostname;

    const isWhiteListed = Object.values(whitelist).some(value => {
      return typeof value === 'string' && value.includes(url);
    });

    return !isWhiteListed;
  }
  return false;
};

export const isLinkNotLocal = (link: string): boolean => {
  if (link?.length) {
    return link.includes('.') && link.toLowerCase().startsWith('http');
  }
  return false;
};

export const isExternalLinkWithFileCheck = (url: string) => {
  if (url?.length) {
    // Create an anchor element to parse the URL
    const link: HTMLAnchorElement = document.createElement('a');
    link.href = url;

    // Get the current page's hostname
    const currentHostname: string = window.location.hostname;

    // Compare the hostname of the link with the current page's hostname
    return link.hostname !== currentHostname;
  }

  return false;
};

const getPatientLangSubDomainWithSlashNormalized = currentLanguageSubDomainWithSlash => {
  const patientLangForced = window.globalSettings?.languageByRole?.patient?.languageShort;
  if (!!patientLangForced && !!currentLanguageSubDomainWithSlash) {
    return `${patientLangForced}/`;
  }

  return currentLanguageSubDomainWithSlash;
};

const getHcpLangSubDomainWithSlashNormalized = currentLanguageSubDomainWithSlash => {
  const hcpLangForced = window.globalSettings?.languageByRole?.hcp?.languageShort;
  if (!!hcpLangForced && !!currentLanguageSubDomainWithSlash) {
    return `${hcpLangForced}/`;
  }

  return currentLanguageSubDomainWithSlash;
};

const getLinkMultilingualData = (): linkMultilingualDataType => {
  const currentPathname = decodeURI(window.location.pathname);
  const { isMultilingual, currentLanguageSubDomain, currentLanguageSubDomainWithSlash } =
    getLinkPathnameLanguageData(currentPathname);
  const linkNormalized = slashTrim(currentPathname);
  const subDomainsArray = linkNormalized?.split('/');
  const arrayLinkWithoutLanguage = isMultilingual ? subDomainsArray.slice(1) : subDomainsArray;
  const patientLangSubDomainWithSlashNormalized = getPatientLangSubDomainWithSlashNormalized(
    currentLanguageSubDomainWithSlash,
  );
  const hcpLangSubDomainWithSlashNormalized = getHcpLangSubDomainWithSlashNormalized(
    currentLanguageSubDomainWithSlash,
  );

  const linkWithoutLanguage = arrayLinkWithoutLanguage.join('/');

  const patientSubDomain = slashTrim(window.globalSettings?.patientLinks?.subDomain) || '';
  const patientSubDomainWithSlash = patientSubDomain.length ? `${patientSubDomain}/` : '';

  const patientProposalCurrentPageLink =
    !isPageTypePatient() && patientSubDomain.length
      ? `/${patientLangSubDomainWithSlashNormalized}${patientSubDomainWithSlash}${linkWithoutLanguage}`
      : '';

  const patientProposalCurrentPageLinkWithoutLang =
    !isPageTypePatient() && patientSubDomain.length
      ? `/${patientSubDomainWithSlash}${linkWithoutLanguage}`
      : '';

  const patientMainPageLink = patientSubDomain.length
    ? `/${patientLangSubDomainWithSlashNormalized}${patientSubDomain}`
    : '';

  const externalPatientLink = window.globalSettings?.patientLinks?.externalLink || '';
  const patientMainPageLinkNormalized =
    patientMainPageLink || `/${patientLangSubDomainWithSlashNormalized}`;
  const patientMainPageLinkNormalizedWithExternal =
    externalPatientLink || patientMainPageLinkNormalized;

  const linkWithoutLanguageAndRole =
    arrayLinkWithoutLanguage[0] === patientSubDomain
      ? arrayLinkWithoutLanguage.slice(1).join('/')
      : arrayLinkWithoutLanguage.join('/');

  const hcpProposalCurrentPageLink = !isPageTypeHcp()
    ? `/${hcpLangSubDomainWithSlashNormalized}${linkWithoutLanguageAndRole}`
    : '';

  const hcpProposalCurrentPageLinkWithoutLang = !isPageTypeHcp()
    ? `/${linkWithoutLanguageAndRole}`
    : '';

  return {
    linkWithoutLanguage,
    isMultilingual,
    currentLanguageSubDomain,
    currentLanguageSubDomainWithSlash,
    patientProposalCurrentPageLink,
    patientProposalCurrentPageLinkWithoutLang,
    patientMainPageLink,
    hcpProposalCurrentPageLink,
    hcpProposalCurrentPageLinkWithoutLang,
    patientMainPageLinkNormalized,
    patientMainPageLinkNormalizedWithExternal,
    linkWithoutLanguageAndRole,
  };
};

export const getMainPageLink = (): string => {
  const { currentLanguageSubDomainWithSlash } = getLinkPathnameLanguageData(
    decodeURI(window.location.pathname),
  );
  const { patientMainPageLink } = getLinkMultilingualData();

  const hcpMainPageLink = getHcpLangSubDomainWithSlashNormalized(currentLanguageSubDomainWithSlash);

  return isPageTypePatient()
    ? patientMainPageLink || `/${currentLanguageSubDomainWithSlash}`
    : `/${hcpMainPageLink}` || `/${currentLanguageSubDomainWithSlash}`;
};

export const addExternalLinkAttribute = ({ el, selector }: addExternalLinkAttributeProps): void => {
  if (!!el && el instanceof HTMLElement) {
    const content = el.querySelector(`${selector}`);
    if (!!content) {
      const links = content.querySelectorAll(`a`);

      links.forEach(linkItem => {
        const isUrlExternal = !!linkItem.getAttribute('href')?.includes('.');
        const isTargetBlank = linkItem.getAttribute('target') === '_blank';

        if (isUrlExternal && isTargetBlank) {
          const originalRelAttribute = linkItem.getAttribute('rel')?.split(' ') || [];
          const relValueArray = [...originalRelAttribute, 'noopener', 'noreferrer'];
          const relAttributeNormalized = [...new Set(relValueArray)].join(' ');
          linkItem.setAttribute('rel', relAttributeNormalized);
        }
      });
    }
  }
};

//determine is provided url is the same as current page url.
export const isCurrentUrl = url => {
  if (!url || typeof url !== 'string') {
    return false;
  }

  const currentURL = new URL(window.location.href);
  const currentPathname = currentURL.pathname;
  const currentHostname = currentURL.hostname;
  const urlObj = new URL(url, currentURL.origin);

  return (
    urlObj.hostname === currentHostname && slashTrim(urlObj.pathname) === slashTrim(currentPathname)
  );
};

//determine if url consist only from hash
export const isUrlHashOnly = url => {
  const urlNormalized = slashTrim(url) || '';
  return urlNormalized.startsWith('#') && urlNormalized.length > 1;
};

export const getLinkWithParameters = ({
  url,
  parameters,
}: getLinkWithParametersAllProps): string => {
  if (!url || !Array.isArray(parameters)) {
    return '';
  }
  const urlObj = new URL(url, window.location.origin);

  parameters.forEach(item => {
    urlObj.searchParams.set(item.name, item.value);
  });

  return urlObj.toString();
};

export const getDomainParamsObj = (): getLinkWithParametersProps => {
  return { name: 'domain', value: window.globalSettings?.domainMachineName };
};

export const isUrlHomePage = (url?: string) => {
  const urlNormalized = url || window.location.href;
  const urlNormalizedPathname = new URL(urlNormalized, window.location.origin).pathname;
  const homePageLinkObject = new URL(getMainPageLink(), window.location.origin);
  return homePageLinkObject.pathname === urlNormalizedPathname;
};

export const getMultilingualPathnameLinkNormalized = (url: string) => {
  if (!url) {
    return '';
  }
  const urlPathName = new URL(url, window.location.origin)?.pathname;
  const urlPathNameNormalized = slashTrim(urlPathName);
  const { currentLanguageSubDomainWithSlash } = getLinkPathnameLanguageData();

  return `/${currentLanguageSubDomainWithSlash}${urlPathNameNormalized}`;
};

export default getLinkMultilingualData;
